import type { ActivityEvent } from '@deskworks/shared-types';
import { Link } from '@tanstack/react-router';
import {
  Activity,
  AlertTriangle,
  Bot,
  CheckCircle2,
  CircleDot,
  ListChecks,
  MessageSquare,
  Play,
  ShieldCheck,
  Wrench,
  Zap,
} from 'lucide-react';
import { RelativeTime } from '@/components/relative-time';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { SessionLink } from '@/components/session-link';

export function activityIcon(type: string) {
  const k = type.toLowerCase();
  if (k.includes('fail') || k.includes('error') || k.includes('lost')) return <AlertTriangle className="text-red-500" />;
  if (k.includes('approval')) return <ShieldCheck className="text-orange-500" />;
  if (k.includes('complete') || k.includes('result') || k.includes('done')) return <CheckCircle2 className="text-emerald-500" />;
  if (k.includes('message') || k.includes('chat')) return <MessageSquare className="text-sky-500" />;
  if (k.includes('plan') || k.includes('progress')) return <ListChecks className="text-indigo-500" />;
  if (k.includes('tool') || k.includes('connection') || k.includes('action')) return <Wrench className="text-zinc-500" />;
  if (k.includes('trigger') || k.includes('desk')) return <Zap className="text-amber-500" />;
  if (k.includes('start') || k.includes('execution')) return <Play className="text-indigo-500" />;
  if (k.includes('agent')) return <Bot className="text-indigo-500" />;
  if (k.includes('status') || k.includes('work_item')) return <CircleDot className="text-zinc-500" />;
  return <Activity className="text-zinc-500" />;
}

export function ActivityRow({
  event,
  workspaceId,
  deskName,
  showLinks = true,
  compact,
}: {
  event: ActivityEvent;
  workspaceId: string;
  deskName?: string;
  showLinks?: boolean;
  compact?: boolean;
}) {
  return (
    <li className={cn('flex gap-3', compact ? 'py-2' : 'py-2.5')}>
      <div className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full border bg-card [&_svg]:size-3.5">
        {activityIcon(event.type)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm break-words">{event.message || <span className="text-muted-foreground">{event.type}</span>}</div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
          <RelativeTime value={event.createdAt} />
          {event.agentName && (
            <>
              <span aria-hidden>·</span>
              <span className="inline-flex items-center gap-1">
                <Bot className="size-3" />
                {event.agentId && showLinks ? (
                  <Link
                    to="/w/$workspaceId/agents/$agentId"
                    params={{ workspaceId, agentId: event.agentId }}
                    className="hover:text-foreground hover:underline"
                  >
                    {event.agentName}
                  </Link>
                ) : (
                  event.agentName
                )}
              </span>
            </>
          )}
          {showLinks && event.deskId && deskName && (
            <>
              <span aria-hidden>·</span>
              <Link
                to="/w/$workspaceId/desks/$deskId"
                params={{ workspaceId, deskId: event.deskId }}
                className="hover:text-foreground hover:underline"
              >
                {deskName}
              </Link>
            </>
          )}
          {showLinks && event.workItemId && (
            <>
              <span aria-hidden>·</span>
              <Link
                to="/w/$workspaceId/work-items/$workItemId"
                params={{ workspaceId, workItemId: event.workItemId }}
                className="hover:text-foreground hover:underline"
              >
                {t('Open Work Item')}
              </Link>
            </>
          )}
          {showLinks && event.executionId && (
            <>
              <span aria-hidden>·</span>
              <SessionLink executionId={event.executionId} workspaceId={workspaceId} />
            </>
          )}
          <span aria-hidden className="hidden sm:inline">·</span>
          <span className="hidden font-mono text-[11px] sm:inline">{event.type}</span>
        </div>
      </div>
    </li>
  );
}

export function ActivityFeed({
  events,
  workspaceId,
  deskNames,
  showLinks = true,
  compact,
}: {
  events: ActivityEvent[];
  workspaceId: string;
  deskNames?: Map<string, string>;
  showLinks?: boolean;
  compact?: boolean;
}) {
  return (
    <ul className="divide-y">
      {events.map((e) => (
        <ActivityRow
          key={e.id}
          event={e}
          workspaceId={workspaceId}
          deskName={deskNames?.get(e.deskId)}
          showLinks={showLinks}
          compact={compact}
        />
      ))}
    </ul>
  );
}
